import React, { useEffect } from 'react'

import { UseFormReturn } from 'react-hook-form'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { Form } from '@fortem-monorepo/react-ui'

import {
  GuidelineFormValues,
  guidelineSchema,
} from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/schema'
import { GuidelineDrawerMode } from '@/pages/funds/ic-guidelines-v2/page'
import {
  useCreateGuidelineMutation,
  useUpdateGuidelineMutation,
} from '@/react-query/mutations/ic-guidelines'

const EMPTY_GUIDELINE: GuidelineFormValues = {
  id: null,
  name: '',
  description: '',
  table: null as unknown as GuidelineFormValues['table'],
  funds: [],
  value_column: '',
  aggregation_function: null,
  threshold_type: 'percentage',
  identifiers: null,
  thresholds: {
    hard_lower: null,
    hard_upper: null,
    soft_lower: null,
    soft_upper: null,
  },
  is_active: true,
}

export interface RenderProps<T = unknown> {
  children?: React.ReactNode | ((props: T) => React.ReactNode)
}

export interface EditGuidelineFormProps extends RenderProps<UseFormReturn<GuidelineFormValues>> {
  defaultValues?: GuidelineFormValues
  mode?: GuidelineDrawerMode
  onSuccess?: () => void
}

export const EditGuidelineForm: React.FC<EditGuidelineFormProps> = ({
  defaultValues,
  mode = 'create',
  onSuccess,
  children,
}) => {
  const form = useFormWithSchema(
    {
      defaultValues: defaultValues ?? { ...EMPTY_GUIDELINE },
      mode: 'all',
    },
    guidelineSchema,
  )

  const { handleSubmit, trigger } = form

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  const createGuidelineMutation = useCreateGuidelineMutation()
  const updateGuidelineMutation = useUpdateGuidelineMutation()

  const submitHandler = handleSubmit(async (_data) => {
    if (mode == 'create') {
      await createGuidelineMutation.mutateAsync({ guideline: _data })
    }

    if (mode == 'edit') {
      await updateGuidelineMutation.mutateAsync({ guideline: _data })
    }

    onSuccess?.()
  })

  useEffect(() => {
    trigger()
  }, [trigger])

  return (
    <div className={'@container relative flex flex-col flex-1'}>
      <Form {...form}>
        <form
          onSubmit={submitHandler}
          id="edit-guideline-form"
          className={'absolute top-0 left-0 w-full h-full flex flex-1 flex-col'}
        >
          {typeof children === 'function' ? children(form) : children}
        </form>
      </Form>
    </div>
  )
}
